import { TICK_HZ, fromSnapshot, staticsOf, step, toSnapshot, vec, type Input, type Inputs, type Player, type PlayerId, type Snapshot, type State, type StaticState, type Vec2 } from '@chessshooter/shared';
import { CLOCK_CATCH_UP, CLOCK_DEAD_BAND_TICKS, CLOCK_SNAP_TICKS, CORRECTION_DECAY_RATE, CORRECTION_EPSILON, CORRECTION_SNAP_DISTANCE, INTERP_DELAY_TICKS, MAX_PENDING_INPUTS, SNAPSHOT_KEEP_TICKS } from './netConstants';

// The latency-hiding half of the online mode, pure data in and out so it is tested without a socket:
// - Prediction runs the own inputs through step() at once and replays the unconfirmed ones on top of
//   every server snapshot (CLAUDE.md, "Netcode in einem Absatz").
// - Correction fades out the jump a misprediction would cause.
// - Interpolation shows the opponent and the rest of the world a little in the past, between two
//   snapshots, and steps the world's own motion (projectiles, beams, crates) forward in between so a
//   20 Hz snapshot stream still moves at 60 Hz.
// Nothing here decides anything: the server's snapshot always wins (rule 5).

const FRAME_MS = 1000 / TICK_HZ;
const IDLE: Input = { seq: 0, moveDir: { x: 0, y: 0 } };
const IDLE_INPUTS: Inputs = { 0: IDLE, 1: IDLE };

function ownInputs(me: PlayerId, input: Input): Inputs {
  return me === 0 ? { 0: input, 1: IDLE } : { 0: IDLE, 1: input };
}

/** fraction of a per-60-Hz-frame rate that `dtMs` of real time amounts to */
function perFrame(rate: number, dtMs: number): number {
  return 1 - Math.pow(1 - rate, dtMs / FRAME_MS);
}

function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

export class Prediction {
  private readonly statics: StaticState;
  private predicted: State;
  private pending: Input[] = [];
  private seq = 0;

  constructor(
    start: State,
    readonly me: PlayerId,
  ) {
    this.statics = staticsOf(start);
    this.predicted = start;
  }

  /** The seq the next own input has to carry; seqs run from 0 per match (and per resume). */
  nextSeq(): number {
    return this.seq;
  }

  /** Applies an own input right away and keeps it until the server has confirmed it. */
  apply(input: Input): void {
    this.pending.push(input);
    if (this.pending.length > MAX_PENDING_INPUTS) this.pending.shift();
    this.predicted = step(this.predicted, ownInputs(this.me, input));
    this.seq = input.seq + 1;
  }

  /**
   * Adopts a snapshot and replays every own input the server has not applied yet. Returns how far
   * the own figure jumped by that (old prediction minus new); zero when the prediction was right.
   */
  reconcile(snapshot: Snapshot, lastProcessedSeq: number): Vec2 {
    const before = this.predicted.players[this.me].pos;
    this.pending = this.pending.filter((i) => i.seq > lastProcessedSeq);
    let s = fromSnapshot(snapshot, this.statics);
    for (const input of this.pending) s = step(s, ownInputs(this.me, input));
    this.predicted = s;
    return vec.sub(before, s.players[this.me].pos);
  }

  state(): State {
    return this.predicted;
  }

  pendingCount(): number {
    return this.pending.length;
  }
}

/** The visual offset that hides a correction: added to the predicted position and faded out per frame. */
export class Correction {
  private offset: Vec2 = { x: 0, y: 0 };

  /** Absorbs a reconciliation jump so the figure keeps its place on screen; a huge one snaps instead. */
  add(error: Vec2): void {
    const next = vec.add(this.offset, error);
    this.offset = vec.len(next) > CORRECTION_SNAP_DISTANCE ? { x: 0, y: 0 } : next;
  }

  advance(dtMs: number): void {
    this.offset = vec.scale(this.offset, 1 - perFrame(CORRECTION_DECAY_RATE, dtMs));
    if (vec.len(this.offset) < CORRECTION_EPSILON) this.offset = { x: 0, y: 0 };
  }

  current(): Vec2 {
    return this.offset;
  }
}

export interface WorldView {
  /** the world at the (whole) render tick; its two players are not to be used */
  world: State;
  /** the opponent between the two snapshots around the render tick */
  opponent: Player;
  renderTick: number;
}

export class Interpolation {
  private readonly statics: StaticState;
  /** ordered by tick, never empty */
  private snapshots: Snapshot[];
  private renderTick: number | null = null;
  private world: State | null = null;
  private worldBaseTick = -1;

  constructor(
    start: State,
    private readonly opponent: PlayerId,
  ) {
    this.statics = staticsOf(start);
    this.snapshots = [toSnapshot(start)];
  }

  push(snapshot: Snapshot): void {
    const latest = this.snapshots[this.snapshots.length - 1];
    if (snapshot.tick <= latest.tick) return; // late or repeated: nothing new
    this.snapshots.push(snapshot);
  }

  /**
   * Runs the render clock on by dtMs. It aims INTERP_DELAY_TICKS behind the newest snapshot: a little
   * faster or slower while off target (never backwards), a jump when far off. It never passes the
   * newest snapshot, so a stalled stream freezes the world instead of inventing it.
   */
  advance(dtMs: number): void {
    const oldest = this.snapshots[0];
    const latest = this.snapshots[this.snapshots.length - 1];
    const target = Math.max(oldest.tick, latest.tick - INTERP_DELAY_TICKS);
    if (this.renderTick === null) {
      this.renderTick = target;
    } else {
      const drift = target - this.renderTick;
      if (Math.abs(drift) > CLOCK_SNAP_TICKS) {
        this.renderTick = target;
      } else {
        const speed = drift > CLOCK_DEAD_BAND_TICKS ? 1 + CLOCK_CATCH_UP : drift < -CLOCK_DEAD_BAND_TICKS ? 1 - CLOCK_CATCH_UP : 1;
        this.renderTick += (dtMs / FRAME_MS) * speed;
      }
    }
    this.renderTick = Math.min(Math.max(this.renderTick, oldest.tick), latest.tick);
    while (this.snapshots.length > 1 && this.snapshots[1].tick <= this.renderTick - SNAPSHOT_KEEP_TICKS) this.snapshots.shift();
  }

  currentTick(): number {
    return this.renderTick ?? this.snapshots[0].tick;
  }

  snapshotCount(): number {
    return this.snapshots.length;
  }

  /** The newest snapshot as a state: what the server last said, for HUD values that must not be guessed. */
  latest(): State {
    return fromSnapshot(this.snapshots[this.snapshots.length - 1], this.statics);
  }

  sample(): WorldView {
    const t = this.currentTick();
    let i = this.snapshots.length - 1;
    while (i > 0 && this.snapshots[i].tick > t) i--;
    const a = this.snapshots[i];
    const b = this.snapshots[i + 1] ?? a;

    const wholeTick = Math.floor(t);
    if (this.world === null || this.worldBaseTick !== a.tick || this.world.tick > wholeTick) {
      this.world = fromSnapshot(a, this.statics);
      this.worldBaseTick = a.tick;
    }
    while (this.world.tick < wholeTick) this.world = step(this.world, IDLE_INPUTS);

    const f = b.tick > a.tick ? (t - a.tick) / (b.tick - a.tick) : 0;
    const pa = a.players[this.opponent];
    const pb = b.players[this.opponent];
    const opponent: Player = a.phase !== b.phase ? pa : { ...pa, pos: lerp(pa.pos, pb.pos, f), vel: lerp(pa.vel, pb.vel, f) };
    return { world: this.world, opponent, renderTick: t };
  }
}

/**
 * The state that is drawn: the own figure from the prediction (plus the fading correction), the
 * opponent interpolated, the world (including all NPC shots) from the delayed snapshot stream.
 * Only the own player shots and the pickups follow the prediction so firing and collecting respond
 * at once, and so does the board:
 * an own move shows at once and snaps back if the server refuses it (phase over, not to move). The
 * phase follows the prediction as well, so input routing follows the same phase as local simulation.
 * The server still decides whether a move arrived before the phase ended.
 */
export function composeScene(prediction: Prediction, correction: Correction, view: WorldView): State {
  const me = prediction.me;
  const predicted = prediction.state();
  // A fresh arena must never be mixed with bodies or effects from the previous phase.
  if (predicted.phase === 'shooter' && view.world.phase !== 'shooter') return {
    ...predicted,
    npcBoxes: [],
    npcs: [],
    projectiles: predicted.projectiles.filter(p => !p.fromNpc && p.ownerId === me),
  };
  const mine: Player = { ...predicted.players[me], pos: vec.add(predicted.players[me].pos, correction.current()) };
  const players: [Player, Player] = me === 0 ? [mine, view.opponent] : [view.opponent, mine];
  const own = <T extends { ownerId: PlayerId }>(list: T[]): T[] => list.filter((x) => x.ownerId === me);
  const foreign = <T extends { ownerId: PlayerId }>(list: T[]): T[] => list.filter((x) => x.ownerId !== me);
  const { world } = view;
  return {
    ...world,
    players,
    pickups: predicted.pickups,
    phase: predicted.phase,
    phaseEndsAtTick: predicted.phaseEndsAtTick,
    transitionTicks: predicted.transitionTicks,
    chess: predicted.chess,
    projectiles: [
      ...world.projectiles.filter(p => p.fromNpc || p.ownerId !== me),
      ...predicted.projectiles.filter(p => !p.fromNpc && p.ownerId === me),
    ],
    beams: [...foreign(world.beams), ...own(predicted.beams)],
    blasts: [...foreign(world.blasts), ...own(predicted.blasts)],
    bursts: [],
  };
}

/** Phase, turn and own slot follow local simulation; clocks and results remain server-authoritative. */
export function composeHudState(prediction: Prediction, latest: State): State {
  const predicted = prediction.state();
  const me = prediction.me;
  const mine = { ...latest.players[me], weapon: predicted.players[me].weapon };
  return {
    ...latest,
    tick: predicted.tick,
    phase: predicted.phase,
    phaseEndsAtTick: predicted.phaseEndsAtTick,
    transitionTicks: predicted.transitionTicks,
    chess: predicted.chess,
    players: me === 0 ? [mine, latest.players[1]] : [latest.players[0], mine],
  };
}
